<script setup lang="ts">
import { cloneDeep } from 'lodash';
import { useDialogPluginComponent } from 'quasar';
import { computed, ref, toRef } from 'vue';
import { useForm } from 'vee-validate';
import * as yup from 'yup';

import type { ContextDto, Locales } from '@/services/types/api.types';

import { Constants } from '@/constants';
import { useContextsStore } from '@/stores';
import { useLanguageValidationErrors } from '@/composables';

import LanguageSwitcher from './LanguageSwitcher.vue';
import LocalizedInput from './LocalizedInput.vue';
import LocalizedSelect from './base/LocalizedSelect.vue';
import LocalizedEditor from './base/LocalizedEditor.vue';
import LocalizedFile from '@/components/base/LocalizedFile.vue';
import LocalizedImageList from '@/components/base/LocalizedImageList.vue';
import { wrapSavingOperation } from '@/helpers/notifications.helper';
import LocalizedFileList from '@/components/base/LocalizedFileList.vue';
import { localizableString } from '@/helpers/validation.helper';
import { useI18n } from 'vue-i18n';
import { ensureLocalizableString } from '@/helpers/form.helper';
import { provideEditLocale } from '@/composables/data-display-utils';

// STORE
const { t } = useI18n();
const contextsStore = useContextsStore();

const props = defineProps<{
  mode: 'add' | 'edit';
  context: ContextDto;
  languageEnabled: Locales[];
}>();

defineEmits([...useDialogPluginComponent.emits]);

const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent();

const initialValues = props.context;
ensureLocalizableString(initialValues, 'content', props.languageEnabled);

const { handleSubmit, errors, meta } = useForm({
  initialValues: initialValues,
  validationSchema: yup.object<ContextDto>({
    title: localizableString(props.languageEnabled),
    type: yup.string().required(),
  }),
});
const languageErrors = useLanguageValidationErrors(errors);

const editModel = ref(cloneDeep(props.context));

const editLocale = provideEditLocale(toRef(() => props.languageEnabled));
const contextTypes = contextsStore.getTypes();
const typeSelected = ref(editModel.value.type);

// FUNCTIONS
function typeChange(value: any) {
  typeSelected.value = value;
}

const dialogTitle = computed(() =>
  props.mode === 'add' ? t('form.dialogs.attachment.create') : t('form.dialogs.attachment.edit'),
);

const onSubmit = handleSubmit(async (values) => {
  await wrapSavingOperation(
    async () => await contextsStore.saveContext({ ...contextsStore.createContextData(values), version: 'v1' }),
    () => onDialogOK(),
  );
});
</script>

<template>
  <q-dialog ref="dialogRef" @hide="onDialogHide" persistent>
    <q-card class="q-dialog-plugin" v-if="editModel" style="width: 600px">
      <q-card-section class="row">
        <div class="text-h6">{{ dialogTitle }}</div>
        <q-space />
        <language-switcher
          v-model="editLocale"
          :has-validation-errors="languageErrors"
          :language-enabled="languageEnabled"
        />
      </q-card-section>
      <q-separator />
      <q-card-section>
        <localized-select
          name="type"
          label="Type"
          :options="contextTypes"
          emit-value
          map-options
          @change="typeChange"
          outlined
          :disabled="props.mode === 'edit'"
        />
        <localized-input
          name="title"
          :label="t('global.label.title')"
          :editLocale="editLocale"
          :language_enabled="languageEnabled"
          outlined
        />
        <div v-if="typeSelected === Constants.Context.Type.Text">
          <q-field :label="t('global.label.content')" stack-label>
            <template v-slot:control>
              <localized-editor
                name="content"
                :label="t('global.label.content')"
                class="q-mt-sm full-width"
                :editLocale="editLocale"
                :language_enabled="languageEnabled"
              />
            </template>
          </q-field>
        </div>
        <div v-else-if="typeSelected === Constants.Context.Type.Image" class="context-image">
          <div class="title">{{ t('context.types.image') }}</div>
          <div class="content-container">
            <localized-image-list name="content" />
            <q-field borderless stack-label :label="t('context.label.image')">
              <template v-slot:control>
                <localized-file name="content_files" class="q-mt-sm full-width" accept="image/*" />
              </template>
            </q-field>
          </div>
        </div>
        <div v-else-if="typeSelected === Constants.Context.Type.Video">
          <localized-input
            name="content"
            :label="t('context.label.video')"
            class="q-mt-sm full-width"
            :editLocale="editLocale"
            :language_enabled="languageEnabled"
          />
        </div>
        <div v-else-if="typeSelected === Constants.Context.Type.Link">
          <localized-input
            name="content"
            :label="t('context.label.link')"
            class="q-mt-sm full-width"
            :editLocale="editLocale"
            :language_enabled="languageEnabled"
          />
        </div>
        <div v-else-if="typeSelected === Constants.Context.Type.File">
          <q-field borderless :label="t('context.label.file')" stack-label>
            <template v-slot:control>
              <localized-file name="content_files" label="" class="q-mt-sm full-width" accept="*" />
            </template>
          </q-field>
          <localized-file-list name="content" />
        </div>
      </q-card-section>
      <q-separator />
      <q-card-actions align="right">
        <q-btn color="primary" flat :label="t('global.cancel')" @click="onDialogCancel" />
        <q-btn color="primary" :label="t('global.save')" @click="onSubmit" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<style lang="scss">
.context-image {
  .title {
    margin-bottom: 5px;
  }

  .content-container {
    .image-list {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      max-height: 300px;
      overflow-y: auto;
    }
  }

  .q-field {
    margin: 5px;
    cursor: pointer;
  }

  //scrollbar styles
  ::-webkit-scrollbar {
    border-radius: 5px;
    width: 7px;
  }

  ::-webkit-scrollbar-track {
    background-color: transparent;
  }

  ::-webkit-scrollbar-thumb {
    background-color: $color-neutral-grey-400-50;
    border-radius: 10px;
  }
}
</style>
